import { ArrowDropDown } from '@mui/icons-material';
import { PickerChangeHandlerContext, PickersShortcutsItem } from '@mui/x-date-pickers';
import {
    DateRangePicker,
    LocalizationProvider as LocalizationProProvider,
    SingleInputDateRangeField,
} from '@mui/x-date-pickers-pro';
import { AdapterDayjs as AdapterDayjsPro } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { DateRange, DateRangeValidationError } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';

export type DatePickerShortcutWithPrevious = PickersShortcutsItem<DateRange<Dayjs>> & { previous?: string };

/**
 * Define common ranges to use as shortcuts for the date picker.
 */
const commonShortcuts: {
    [key: string]: DatePickerShortcutWithPrevious;
} = {
    // -----------------------------------------------------------------------------------------------------------------
    // Current ranges.
    today: {
        label: 'Today',
        previous: 'yesterday',
        getValue: () => {
            return [dayjs().startOf('day'), dayjs().endOf('day')];
        },
    },
    thisWeek: {
        label: 'This Week',
        previous: 'lastWeek',
        getValue: () => {
            return [dayjs().startOf('week').startOf('day'), dayjs().endOf('day')];
        },
    },
    thisMonth: {
        label: 'This Month',
        previous: 'lastMonth',
        getValue: () => {
            return [dayjs().startOf('month').startOf('day'), dayjs().endOf('day')];
        },
    },
    thisQuarter: {
        label: 'This Quarter',
        previous: 'lastQuarter',
        getValue: () => {
            return [dayjs().startOf('quarter').startOf('day'), dayjs().endOf('day')];
        },
    },
    thisYear: {
        label: 'This Year',
        previous: 'lastYear',
        getValue: () => {
            return [dayjs().startOf('year').startOf('day'), dayjs().endOf('day')];
        },
    },
    last30Days: {
        label: 'Last 30 Days',
        getValue: () => {
            return [dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')];
        },
    },
    last60Days: {
        label: 'Last 60 Days',
        getValue: () => {
            return [dayjs().subtract(60, 'day').startOf('day'), dayjs().endOf('day')];
        },
    },
    last90Days: {
        label: 'Last 90 Days',
        getValue: () => {
            return [dayjs().subtract(90, 'day').startOf('day'), dayjs().endOf('day')];
        },
    },
    // -----------------------------------------------------------------------------------------------------------------
    // Previous ranges.
    yesterday: {
        label: 'Yesterday',
        getValue: () => {
            const yesterday = dayjs().subtract(1, 'day').startOf('day');
            return [yesterday, yesterday.endOf('day')];
        },
    },
    lastWeek: {
        label: 'Last Week',
        getValue: () => {
            const lastWeek = dayjs().subtract(1, 'week').startOf('week');
            return [lastWeek, lastWeek.endOf('week')];
        },
    },
    lastMonth: {
        label: 'Last Month',
        getValue: () => {
            const lastMonth = dayjs().subtract(1, 'month').startOf('month');
            return [lastMonth, lastMonth.endOf('month')];
        },
    },
    lastQuarter: {
        label: 'Last Quarter',
        getValue: () => {
            const lastQuarter = dayjs().subtract(1, 'quarter').startOf('quarter');
            return [lastQuarter, lastQuarter.endOf('quarter')];
        },
    },
    lastYear: {
        label: 'Last Year',
        getValue: () => {
            const lastYear = dayjs().subtract(1, 'year').startOf('year');
            return [lastYear, lastYear.endOf('year')];
        },
    },
};

// Allow TypeScript to properly understand a .filter() call that removes nulls.
const isNotNull = <T,>(value: T | null | undefined): value is T => value !== null;

/**
 * Generate shortcut item instances for the date picker from a list of shortcuts.
 * @param shortcuts Shortcut instances or strings referencing the common shortcuts.
 * @returns An array of shortcut instances.
 */
function getShortcuts(shortcuts: (PickersShortcutsItem<DateRange<Dayjs>> | string)[] | undefined) {
    if (!shortcuts) {
        return undefined;
    }

    return shortcuts
        .map((shortcut) => {
            if (typeof shortcut === 'string') {
                // It is one of the system shortcuts.
                return commonShortcuts[shortcut];
            } else {
                // It is a picker shortcut.
                return shortcut;
            }
        })
        .filter(isNotNull);
}

/**
 * Given a shortcut, return the date range it represents.
 * @param shortcut The shortcut to evaluate.
 * @returns A date range.
 */
export function getShortcutRange(shortcut: string | null | undefined) {
    if (shortcut) {
        return (
            commonShortcuts[shortcut]?.getValue({
                isValid: () => true,
            }) ?? null
        );
    }
    return null;
}

/**
 * Given a date range, return the previous range that covers the same number of days.
 * @param range The current date range.
 * @returns The previous date range.
 */
export function previousRangeFromCurrentRange(range: DateRange<Dayjs>) {
    if (range[0] && range[1]) {
        const dayDiff = range[1].diff(range[0], 'days') + 1,
            previousRangeEnd = range[0].subtract(1, 'day').endOf('day');
        return [previousRangeEnd.subtract(dayDiff, 'day').startOf('day'), previousRangeEnd];
    }
    return [null, null];
}

/**
 * A date ranger picker component with a single input field and easier support for shortcuts.
 * @param props.clearable Make the input field clearable.
 * @param props.clearable Array of shortcuts to display in the popover.
 * @returns
 */
export function DateRangePicker2(props: {
    range: DateRange<Dayjs>;
    min?: Dayjs;
    max?: Dayjs;
    label: string;
    disableFuture?: boolean;
    clearable?: boolean;
    shortcuts?: (PickersShortcutsItem<DateRange<Dayjs>> | string)[];
    onAccept?: (range: DateRange<Dayjs>, context: PickerChangeHandlerContext<DateRangeValidationError>) => void;
    onChange?: (range: DateRange<Dayjs>, context: PickerChangeHandlerContext<DateRangeValidationError>) => void;
}) {
    return (
        <LocalizationProProvider dateAdapter={AdapterDayjsPro}>
            <DateRangePicker
                label={props.label}
                value={props.range}
                minDate={props.min}
                maxDate={props.max}
                onAccept={props.onAccept}
                onChange={props.onChange}
                disableFuture={props.disableFuture}
                closeOnSelect={false}
                slots={{
                    field: SingleInputDateRangeField,
                }}
                slotProps={{
                    textField: {
                        fullWidth: true,
                        InputProps: {
                            endAdornment: <ArrowDropDown />,
                        },
                        size: 'small',
                    },
                    shortcuts: {
                        items: getShortcuts(props.shortcuts),
                    },
                    field: {
                        clearable: props.clearable,
                    },
                    actionBar: {
                        hidden: false,
                        actions: ['cancel', 'accept'],
                    },
                }}
            />
        </LocalizationProProvider>
    );
}
